import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './shared/AppShell';
import Home from './pages/Home';
import MainDashboard from './pages/MainDashboard';
import FamilyDashboard from './pages/FamilyDashboard';
import FamilyDashboardDetail from './pages/FamilyDashboardDetail';
import FamilySegmentationFramework from './pages/FamilySegmentationFramework';
import FamilySegmentDetail from './pages/FamilySegmentDetail';
import FamilyPoorVulnerable from './pages/FamilyPoorVulnerable';
import FamilyFarmer from './pages/FamilyFarmer';
import FamilyLandlessLabour from './pages/FamilyLandlessLabour';
import FamilyYouth from './pages/FamilyYouth';
import FamilyWomenLed from './pages/FamilyWomenLed';
import FamilySchoolChildren from './pages/FamilySchoolChildren';
import FamilyHealthPriority from './pages/FamilyHealthPriority';
import FamilySeniorCitizen from './pages/FamilySeniorCitizen';
import FamilyLivestock from './pages/FamilyLivestock';
import FamilyMigrant from './pages/FamilyMigrant';
import FamilyInfrastructureGap from './pages/FamilyInfrastructureGap';
import FamilyDigital from './pages/FamilyDigital';
import FamilyWelfareGap from './pages/FamilyWelfareGap';
import FamilyNutritionPriority from './pages/FamilyNutritionPriority';
import FamilyPriorityIndex from './pages/FamilyPriorityIndex';
import FamilyPersonasFramework from './pages/FamilyPersonasFramework';
import FamilyPersonaDetail from './pages/FamilyPersonaDetail';
import FamilyAspirationalFarmer from './pages/FamilyAspirationalFarmer';
import FamilyStrugglingLabour from './pages/FamilyStrugglingLabour';
import FamilyEmergingYouth from './pages/FamilyEmergingYouth';
import FamilyWomensEmpowerment from './pages/FamilyWomensEmpowerment';
import FamilyWelfareExcluded from './pages/FamilyWelfareExcluded';
import FamilyInfrastructureDeficit from './pages/FamilyInfrastructureDeficit';
import FamilyHealthyProgressive from './pages/FamilyHealthyProgressive';
import CategoryPage from './pages/CategoryPage';
import RulePage from './pages/RulePage';
import Dashboard from './pages/Dashboard';
import SundargarhDashboard from './pages/SundargarhDashboard';
import SundargarhHub from './pages/SundargarhHub';
import SundargarhCategoryPage from './pages/SundargarhCategoryPage';
import SundargarhRulePage from './pages/SundargarhRulePage';
import SundargarhAnalytics from './pages/SundargarhAnalytics';
import NormalDashboard from './pages/NormalDashboard';
import NormalAnalytics from './pages/NormalAnalytics';
import Analytics from './pages/Analytics';
import GpsAccuracy from './pages/GpsAccuracy';
import LiveApiData from './pages/LiveApiData';
import Subhadra from './pages/Subhadra';
import Member from './pages/Member';
import HouseholdRoster from './pages/HouseholdRoster';
import GenderRoster from './pages/GenderRoster';
import Indicators from './pages/Indicators';
import Anganwadi from './pages/Anganwadi';
import SchoolCount from './pages/SchoolCount';
import SmallHouseholds from './pages/SmallHouseholds';
import MultiSpouse from './pages/MultiSpouse';
import AgeEducation from './pages/AgeEducation';
import InstitutionalDelivery from './pages/InstitutionalDelivery';
import HealthInsurance from './pages/HealthInsurance';
import DuplicateMember from './pages/DuplicateMember';
import Upload from './pages/Upload';
import Users from './pages/Users';
import Login from './pages/Login';
import './theme/theme.css';

// Route guard. Redirects unauthenticated users to /login and users lacking the
// required role back to Home (D4: executive is read-only, no Upload/Users).
// A district-scoped user (e.g. Sundargarh) can only open that district's
// routes (`district` prop); everyone else is sent to their district's hub.
const ProtectedRoute = ({ children, allowedRoles = ['admin', 'executive'], district }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.district && user.district !== district) {
    return <Navigate to={`/${user.district}-hub`} replace />;
  }
  if (!allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const ALL = ['admin', 'executive'];
  const ADMIN = ['admin'];

  return (
    <AppShell>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to={user.district ? `/${user.district}-hub` : '/'} replace />} />

        <Route path="/" element={<ProtectedRoute allowedRoles={ALL}><Home /></ProtectedRoute>} />
        <Route path="/main-dashboard" element={<ProtectedRoute allowedRoles={ALL}><MainDashboard /></ProtectedRoute>} />
        <Route path="/family-dashboard" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyDashboard /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework" element={<ProtectedRoute allowedRoles={ADMIN}><FamilySegmentationFramework /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/poor-vulnerable" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyPoorVulnerable /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/farmer" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyFarmer /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/landless-labour" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyLandlessLabour /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/youth" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyYouth /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/women-led-development" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyWomenLed /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/school-going-children" element={<ProtectedRoute allowedRoles={ADMIN}><FamilySchoolChildren /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/health-priority" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyHealthPriority /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/senior-citizen" element={<ProtectedRoute allowedRoles={ADMIN}><FamilySeniorCitizen /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/livestock" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyLivestock /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/migrant" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyMigrant /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/infrastructure-gap" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyInfrastructureGap /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/digital" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyDigital /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/welfare-gap" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyWelfareGap /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/nutrition-priority" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyNutritionPriority /></ProtectedRoute>} />
        <Route path="/family-dashboard/segmentation-framework/:segmentId" element={<ProtectedRoute allowedRoles={ADMIN}><FamilySegmentDetail /></ProtectedRoute>} />
        <Route path="/family-dashboard/priority-index" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyPriorityIndex /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyPersonasFramework /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/aspirational-farmer" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyAspirationalFarmer /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/struggling-labour" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyStrugglingLabour /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/emerging-youth" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyEmergingYouth /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/womens-empowerment" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyWomensEmpowerment /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/welfare-excluded" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyWelfareExcluded /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/infrastructure-deficit" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyInfrastructureDeficit /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/healthy-progressive" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyHealthyProgressive /></ProtectedRoute>} />
        <Route path="/family-dashboard/personas/:personaId" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyPersonaDetail /></ProtectedRoute>} />
        <Route path="/family-dashboard/:id" element={<ProtectedRoute allowedRoles={ADMIN}><FamilyDashboardDetail /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={ALL}><Dashboard /></ProtectedRoute>} />
        <Route path="/sundargarh-dashboard" element={<ProtectedRoute allowedRoles={ALL} district="sundargarh"><SundargarhDashboard /></ProtectedRoute>} />
        <Route path="/sundargarh-hub" element={<ProtectedRoute allowedRoles={ALL} district="sundargarh"><SundargarhHub /></ProtectedRoute>} />
        <Route path="/sundargarh-category/:id" element={<ProtectedRoute allowedRoles={ALL} district="sundargarh"><SundargarhCategoryPage /></ProtectedRoute>} />
        <Route path="/sundargarh-rules/:id" element={<ProtectedRoute allowedRoles={ALL} district="sundargarh"><SundargarhRulePage /></ProtectedRoute>} />
        <Route path="/sundargarh-analytics" element={<ProtectedRoute allowedRoles={ALL} district="sundargarh"><SundargarhAnalytics /></ProtectedRoute>} />
        <Route path="/normal-dashboard" element={<ProtectedRoute allowedRoles={ALL}><NormalDashboard /></ProtectedRoute>} />
        <Route path="/normal-analytics" element={<ProtectedRoute allowedRoles={ALL}><NormalAnalytics /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute allowedRoles={ALL}><Analytics /></ProtectedRoute>} />
        <Route path="/gps-accuracy" element={<ProtectedRoute allowedRoles={ALL}><GpsAccuracy /></ProtectedRoute>} />
        <Route path="/live-api-data" element={<ProtectedRoute allowedRoles={ALL}><LiveApiData /></ProtectedRoute>} />
        <Route path="/category/:id" element={<ProtectedRoute allowedRoles={ALL}><CategoryPage /></ProtectedRoute>} />
        <Route path="/rules/:id" element={<ProtectedRoute allowedRoles={ALL}><RulePage /></ProtectedRoute>} />
        <Route path="/subhadra" element={<ProtectedRoute allowedRoles={ALL}><Subhadra /></ProtectedRoute>} />
        <Route path="/member" element={<ProtectedRoute allowedRoles={ALL}><Member /></ProtectedRoute>} />
        <Route path="/household-roster" element={<ProtectedRoute allowedRoles={ALL}><HouseholdRoster /></ProtectedRoute>} />
        <Route path="/gender-roster" element={<ProtectedRoute allowedRoles={ALL}><GenderRoster /></ProtectedRoute>} />
        <Route path="/indicators" element={<ProtectedRoute allowedRoles={ALL}><Indicators /></ProtectedRoute>} />
        <Route path="/anganwadi" element={<ProtectedRoute allowedRoles={ALL}><Anganwadi /></ProtectedRoute>} />
        <Route path="/school-count" element={<ProtectedRoute allowedRoles={ALL}><SchoolCount /></ProtectedRoute>} />
        <Route path="/small-households" element={<ProtectedRoute allowedRoles={ALL}><SmallHouseholds /></ProtectedRoute>} />
        <Route path="/multi-spouse" element={<ProtectedRoute allowedRoles={ALL}><MultiSpouse /></ProtectedRoute>} />
        <Route path="/age-education" element={<ProtectedRoute allowedRoles={ALL}><AgeEducation /></ProtectedRoute>} />
        <Route path="/institutional-delivery" element={<ProtectedRoute allowedRoles={ALL}><InstitutionalDelivery /></ProtectedRoute>} />
        <Route path="/health-insurance" element={<ProtectedRoute allowedRoles={ALL}><HealthInsurance /></ProtectedRoute>} />
        <Route path="/duplicate-member" element={<ProtectedRoute allowedRoles={ALL}><DuplicateMember /></ProtectedRoute>} />

        <Route path="/upload" element={<ProtectedRoute allowedRoles={ADMIN}><Upload /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute allowedRoles={ADMIN}><Users /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to={user?.district ? `/${user.district}-hub` : '/'} replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}
